import { useEffect, useState } from "react";
import axios from "axios";
import { BellRing, BellOff, Monitor, Send, Smartphone, Trash2 } from "lucide-react";
import { API } from "../lib/apiClient";

const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIos = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone);

function b64ToUint8(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const TOGGLES = [
  ["pushRenewals", "Yenileme hatırlatmaları", "Vadesi yaklaşan poliçeler (e-postadaki gün seçimleriyle aynı)"],
  ["pushInspections", "Muayene hatırlatmaları", "Muayenesi yaklaşan araçlar"],
  ["pushBackup", "Haftalık yedek gönderildi", "Yedek e-postası iletildiğinde"],
  ["pushMonthly", "Aylık portföy özeti gönderildi", "Aylık PDF özeti iletildiğinde"],
  ["pushBirthdays", "Müşteri doğum günleri", "Doğum günü olan müşteriler, bildirim saatinde"],
  ["pushNewQuote", "Yeni online teklif talebi", "Müşteri online teklif formunu doldurduğunda anında"],
  ["pushAppointments", "Randevu hatırlatma", "Randevudan 1 saat önce ve sabah 08:00'de günün randevu özeti"],
  ["pushQuoteViewed", "Teklif görüntüleme", "Müşteri gönderilen teklif karşılaştırma linkini her açtığında"],
  ["pushQuoteApproved", "Teklif onay", "Müşteri online teklif linkinden bir teklifi seçip onayladığında"],
  ["pushEmailSent", "Diğer e-posta gönderimleri", "Yenileme, teklif ve deneme e-postaları (otomatik veya elle) iletildiğinde"],
];

export function PushSettingsCard() {
  const [prefs, setPrefs] = useState(null);
  const [devices, setDevices] = useState(0);
  const [list, setList] = useState([]);
  const [myEndpoint, setMyEndpoint] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState(supported ? Notification.permission : "unsupported");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [p, d] = await Promise.all([axios.get(`${API}/user/preferences`), axios.get(`${API}/push/devices`)]);
    setPrefs(p.data);
    setList(d.data);
    setDevices(d.data.length);
    if (supported) {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      setMyEndpoint(sub?.endpoint || "");
      setSubscribed(!!sub && d.data.some((x) => x.endpoint === sub.endpoint));
    }
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const wrap = async (fn) => {
    setBusy(true); setMsg("");
    try { await fn(); } catch (e) { setMsg(e?.response?.data?.detail || e?.message || "Bir hata oluştu"); }
    finally { setBusy(false); }
  };

  const enable = () => wrap(async () => {
    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm !== "granted") throw new Error("Bildirim izni verilmedi. Tarayıcı/telefon ayarlarından izin verebilirsiniz.");
    const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.register("/sw.js"));
    await navigator.serviceWorker.ready;
    const { data } = await axios.get(`${API}/push/vapid-public-key`);
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.publicKey) });
    const res = await axios.post(`${API}/push/subscribe`, { ...sub.toJSON(), userAgent: navigator.userAgent });
    setDevices(res.data.devices);
    await load();
    setMsg("Bu cihazda bildirimler açıldı.");
  });

  const disable = () => wrap(async () => {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && (await reg.pushManager.getSubscription());
    if (sub) {
      const res = await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint });
      await sub.unsubscribe();
      setDevices(res.data.devices);
    }
    await load();
    setMsg("Bu cihazda bildirimler kapatıldı.");
  });

  const test = () => wrap(async () => {
    const { data } = await axios.post(`${API}/push/test`);
    setMsg(`Deneme bildirimi ${data.delivered} cihaza gönderildi.`);
  });

  const testAppt = () => wrap(async () => {
    const { data } = await axios.post(`${API}/appointments/test-reminder`, {});
    setMsg(`Deneme randevu hatırlatması ${data.delivered} cihaza gönderildi.`);
  });

  const removeDevice = (d) => wrap(async () => {
    if (!window.confirm(`"${d.name}" cihazı bildirim listesinden kaldırılsın mı?`)) return;
    await axios.delete(`${API}/push/devices/${d.id}`);
    if (d.endpoint === myEndpoint) {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) await sub.unsubscribe();
    }
    await load();
    setMsg(`${d.name} kaldırıldı.`);
  });

  const update = (patch) => wrap(async () => {
    const { data } = await axios.put(`${API}/user/preferences`, patch);
    setPrefs(data);
  });

  return (
    <section className="panel settings-card push-card" data-testid="push-settings-card">
      <div className="settings-card-head"><Smartphone size={16} /> <h2>Telefon bildirimleri</h2></div>
      {!supported && <p className="privacy-note" data-testid="push-unsupported">Bu tarayıcı bildirimleri desteklemiyor.{isIos ? " iPhone'da Safari'den Paylaş → \"Ana Ekrana Ekle\" ile uygulamayı kurup oradan açın (iOS 16.4+)." : ""}</p>}
      {supported && isIos && !isStandalone && <p className="privacy-note" data-testid="push-ios-hint">iPhone'da bildirim için uygulamayı Safari'den Paylaş → "Ana Ekrana Ekle" ile kurup ana ekrandaki simgeden açın.</p>}
      <div className="push-status-row">
        <span className={`push-pill ${subscribed ? "on" : "off"}`} data-testid="push-device-status">{subscribed ? "Bu cihazda açık" : "Bu cihazda kapalı"}</span>
        <span className="push-devices" data-testid="push-device-count">Kayıtlı cihaz: {devices}</span>
        {permission === "denied" && <span className="push-denied" data-testid="push-permission-denied">İzin engellenmiş · tarayıcı ayarlarından açın</span>}
      </div>
      <div className="settings-actions">
        {supported && !subscribed && <button className="primary-button" onClick={enable} disabled={busy} data-testid="push-enable-button"><BellRing size={14} /> Bildirimlere izin ver</button>}
        {supported && subscribed && <button className="outline-button" onClick={disable} disabled={busy} data-testid="push-disable-button"><BellOff size={14} /> Bu cihazda kapat</button>}
        <button className="outline-button" onClick={test} disabled={busy || devices === 0} data-testid="push-test-button"><Send size={14} /> Deneme bildirimi gönder</button>
        <button className="outline-button" onClick={testAppt} disabled={busy || devices === 0} data-testid="push-test-appointment-button"><Send size={14} /> Randevu hatırlatma testi</button>
      </div>
      {msg && <small className="signature-hint" data-testid="push-msg">{msg}</small>}
      {list.length > 0 && (
        <ul className="push-device-list" data-testid="push-device-list">
          {list.map((d) => (
            <li key={d.id} data-testid={`push-device-${d.id}`}>
              {/iPhone|iPad|Android/.test(d.name) ? <Smartphone size={16} /> : <Monitor size={16} />}
              <div>
                <strong>{d.name}{d.endpoint === myEndpoint && <em className="push-this"> · Bu cihaz</em>}</strong>
                <span>Eklendi: {d.createdAt ? new Date(d.createdAt).toLocaleDateString("tr-TR") : "—"}{d.lastPushAt ? ` · Son bildirim: ${new Date(d.lastPushAt).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}` : ""}</span>
              </div>
              <button className="icon-button" onClick={() => removeDevice(d)} disabled={busy} title="Kaldır" data-testid={`push-device-remove-${d.id}`}><Trash2 size={15} /></button>
            </li>
          ))}
        </ul>
      )}
      {prefs && (
        <div className="push-prefs">
          <label className="push-hour">
            <span>Günlük hatırlatma saati</span>
            <select value={prefs.pushHour} disabled={busy} onChange={(e) => update({ pushHour: Number(e.target.value) })} data-testid="push-hour-select">
              {Array.from({ length: 24 }).map((_, tr) => <option key={tr} value={(tr + 21) % 24}>{`${String(tr).padStart(2, "0")}:00`}</option>)}
            </select>
          </label>
          {TOGGLES.map(([key, label, hint]) => (
            <label key={key} className="push-toggle">
              <input type="checkbox" checked={!!prefs[key]} disabled={busy} onChange={(e) => update({ [key]: e.target.checked })} data-testid={`push-toggle-${key}`} />
              <span><strong>{label}</strong><em>{hint}</em></span>
            </label>
          ))}
        </div>
      )}
    </section>
  );
}
